import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import ApiService from "../../api/ApiServices";
import LoadImage from "../../assets/loader.gif";
import { AppPaths } from "../../utils/AppPaths";
import AlertModal from "../AlertModal";

type PrizeDetails = {
	id: string;
	title: string;
	writeup?: string;
	logo?: string;
	image?: string;
	pointsRequired: number;
};

type ClaimPrizeLocationState = {
	prizeId?: string;
	prizeImage?: string;
	prizeTitle?: string;
	pointsRequired?: number;
	writeup?: string;
};

const ClaimPrize = () => {
	const navigate = useNavigate();
	const location = useLocation();
	const locationState = (location.state || {}) as ClaimPrizeLocationState;
	const [loading, setLoading] = useState<boolean>(true);
	const [claiming, setClaiming] = useState<boolean>(false);
	const [prize, setPrize] = useState<PrizeDetails | null>(null);
	const [showAlert, setShowAlert] = useState<boolean>(false);
	const [alertMessage, setAlertMessage] = useState<string>("");

	const openAlert = (message: string) => {
		setAlertMessage(message);
		setShowAlert(true);
	};

	const prizeId = useMemo(() => {
		if (locationState?.prizeId) {
			return String(locationState.prizeId);
		}

		const query = new URLSearchParams(location.search);
		return query.get("prizeId") || "";
	}, [location.search, locationState?.prizeId]);

	useEffect(() => {
		const fetchPrize = async () => {
			if (!prizeId) {
				openAlert("Prize not found.");
				navigate(AppPaths.PRIZES, { replace: true });
				return;
			}

			setLoading(true);
			try {
				const response = await ApiService.getPrizeById(prizeId);
				const source = response?.data?.data ?? response?.data ?? response ?? {};
				setPrize({
					id: String(source?._id || source?.id || prizeId),
					title: source?.title || locationState?.prizeTitle || "Prize",
					writeup: source?.writeup || source?.description || locationState?.writeup,
					logo: source?.logo || locationState?.prizeImage,
					image: source?.image || locationState?.prizeImage,
					pointsRequired: Number(source?.pointsRequired || locationState?.pointsRequired || 0),
				});
			} catch (error: any) {
				openAlert(error?.message || "Unable to load prize details.");
				navigate(AppPaths.PRIZES, { replace: true });
			} finally {
				setLoading(false);
			}
		};

		fetchPrize();
	}, [navigate, prizeId, locationState?.pointsRequired, locationState?.prizeImage, locationState?.prizeTitle, locationState?.writeup]);

	const onClaimPrize = async () => {
		if (!prize?.id || claiming) {
			return;
		}

		setClaiming(true);
		try {
			const response = await ApiService.claimPrize({ prizeId: prize.id });
			navigate(AppPaths.REWARD_CLAIMED, {
				replace: true,
				state: {
					prizeTitle: prize.title,
					prizeImage: prize.image || prize.logo,
					pointsRequired: prize.pointsRequired,
					message: response?.message,
				},
			});
		} catch (error: any) {
			openAlert(error?.message || "Unable to claim prize.");
		} finally {
			setClaiming(false);
		}
	};

	if (loading) {
		return (
			<div className="flex justify-center items-center h-[70vh]">
				<img width="100px" className="center" src={LoadImage} alt="loader" />
			</div>
		);
	}

	if (!prize) {
		return null;
	}

	const prizeImage = prize.image || require("../../assets/star.png");

	return (
		<div className="min-h-screen px-5 pb-10">
			<div className="flex items-center relative px-1 py-4">
				<img
					onClick={() => navigate(-1)}
					alt="Back"
					className="home-frag-pic-backbtn mr-2 cursor-pointer"
					src={require("../../assets/arrow-left.png")}
				/>
				<div className="btn-onb4-text absolute left-1/2 -translate-x-1/2 text-center">Claim Reward</div>
			</div>

			<div className="pt-4 flex justify-center">
				<div className="w-[220px] h-[220px] flex items-center justify-center">
					<img src={prizeImage} alt={prize.title} className="max-w-full max-h-full object-contain" />
				</div>
			</div>

			<div className="text-center mt-3 inter-regular-14" style={{ fontWeight: 500 }}>
				{prize.title} <span className="text-[#F73294]">({prize.pointsRequired} Pts)</span>
			</div>
			<div className="text-center inter-regular-14 leading-5 mt-2">
				{prize.writeup || "The gift card will be sent to your registered email within 24 hours."}
			</div>

			<div className="mt-8 grid grid-cols-2 gap-4">
				<button
					type="button"
					onClick={() => navigate(-1)}
					className="secondary-btn h-9 inter-14-bold"
				>
					CANCEL
				</button>
				<button
					type="button"
					onClick={onClaimPrize}
					disabled={claiming}
					className={`primary-btn h-9 ${claiming ? "opacity-70 cursor-not-allowed" : "cursor-pointer"}`}
                    style={{ minHeight: "35px" }}
				>
					<div className="primary-btn-text" style={{fontSize: "14px", fontWeight: 600 }}>{claiming ? "CLAIMING..." : "CLAIM NOW!"}</div>
				</button>
			</div>
			<AlertModal
				visible={showAlert}
				title="Oops!"
				message={alertMessage}
				onConfirm={() => setShowAlert(false)}
			/>
		</div>
	);
};

export default ClaimPrize;
